import i18n from "@/i18n";
import OrderedPackageCount from "@/state/models/orderedPackageCount";
import OrderedPackageDto from "@/state/models/orderedPackageDto";
import TalentModel from "@/state/models/talentModel";
import { toastrService } from "../notification/toastrService";
import { restService } from "../restService";

const orderedPackageUrl = restService.buildURL(process.env.VUE_APP_API_ORDERED_PACKAGES_URL);

/**
 * Return array of used packages and the amount of times
 * they were used
 *
 * @param tenantId UUID of the tenant you want the packages for
 * @param departmentId Array of UUIDs of the department(s)
 * @param yearMonth YYYY-MM
 */
async function getPackageCount(
  tenantId: string = "",
  departmentId: string[]|null = null,
  yearMonth: Date|null = null
  ) {
  const queryParameters: URLSearchParams = _getQueryParameters(tenantId, departmentId, _getYearMonth(yearMonth), {});

  const url: string = `${orderedPackageUrl}/count-per-package?${queryParameters.toString()}`;

  const packageCount: OrderedPackageCount[] = await restService.get(url);

  if (!packageCount) {
    toastrService.showError(i18n.t("package.package-count-error"));
    return [];
  }

  return packageCount;
}

async function fetchAllPackages(
  tenantId: string = "",
  departmentId: string[] | null = null,
  yearMonth: Date | null = null,
  pagination: Object = {size: 5, page: 1}
) {

  const queryParameters: URLSearchParams = _getQueryParameters(tenantId, departmentId, _getYearMonth(yearMonth), pagination);

  const url: string = `${orderedPackageUrl}?${queryParameters.toString()}`;
  const allPackages: any = await restService.get(url);

  if (!allPackages) {
    toastrService.showError(i18n.t("package.package-overview-error"));
    return [];
  }

  return allPackages;
}

/**
 * Either return the yearMonth given or take the current date
 * @param yearMonth
 * @private
 */
function _getYearMonth(yearMonth: Date | null): string {
  let date: Date = new Date();

  if (yearMonth) {
    date = yearMonth;
  }

  const currentYear: number = date.getFullYear()
  const currentMonth: number = date.getMonth() + 1;

  return `${currentYear}-${currentMonth}`;
}

/**
 * Return a URLSearchParams object to easily
 * add query params dynamically to the requests
 *
 * @private
 * @param tenantId
 * @param departmentId
 * @param yearMonth
 * @param pagination
 */
function _getQueryParameters(
  tenantId: string = "",
  departmentId: string[] | null = null,
  yearMonth: string | null = null,
  pagination: any
): URLSearchParams {
  let queryParametersRaw: any = {};

  if (tenantId) {
    queryParametersRaw.companyId = tenantId;
  }

  if (departmentId) {
    queryParametersRaw.departmentId = departmentId;
  }

  if (yearMonth) {
    queryParametersRaw.yearMonth = yearMonth;
  }

  // Pagination
  if (pagination && pagination.size) {
    queryParametersRaw.size = pagination.size;
  }

  if (pagination && pagination.page >= 0) {
    queryParametersRaw.page = pagination.page;
  }

  return new URLSearchParams(queryParametersRaw);
}

/**
 * Return details of the chosen package
 *
 * @param packageId
 */
async function fetchPackageDetails(packageId: string) {
  const url: string = `${orderedPackageUrl}/${packageId}`;
  const packageDetails: OrderedPackageDto[] = await restService.get(url);

  if (!packageDetails) {
    toastrService.showError(i18n.t("package.package-details-error"));
    return [];
  }

  return packageDetails;
}

/**
 * Return talents of the given package
 *
 * @param packageId
 */
async function fetchTalentsForPackage(packageId: string, pagination: Object = { size: 5, page: 1 }) {
  const queryParameters: URLSearchParams = _getQueryParameters("", null, null, pagination);
  const url: string = `${orderedPackageUrl}/${packageId}/talents?${queryParameters.toString()}`;

  const talents: TalentModel[] = await restService.get(url);

  if (!talents) {
    toastrService.showError(i18n.t("package.package-details-error"));
    return [];
  }

  return talents;
}


export const packageService = {
  getPackageCount,
  fetchAllPackages,
  fetchPackageDetails,
  fetchTalentsForPackage,
}